import { OpeningType } from '~/components/ThreeDModel/furnitureConfig'
import type { WardrobeColumnConfiguration } from '~/types/wardrobeConfigurationTypes'
import { computeSidePanelDimensions } from '~/utils/computeSidePanelDimensions'
import { computeTopPanelDimensions } from '~/utils/computeTopPanelDimensions'
import { computeColumnDividerDimensions } from '~/utils/computeColumnDividerDimensions'
import { computeDoorDimensions } from '~/utils/computeDoorDimensions'
import { computeInteriorPanels } from '~/utils/wardrobeInteriorPanels'

export interface PanelPart {
  width: number
  height: number
  quantity: number
  /**
   * True for the NewCollectionHandle full-height vertical strip. It is a real
   * PAL panel (kept in `doors` for material cutting) but it is NOT a door
   * facade, so it must be excluded from the `doorsNumber` count sent to pricing.
   */
  isHandle?: boolean
}

export interface WardrobeParts {
  thickness: number
  sidePanels: {
    left: PanelPart
    right: PanelPart
  }
  topPanel: PanelPart // TODO: recalculate top panels, when 1 column divider is larger
  plinth: PanelPart
  backPanel: PanelPart
  columnDividers: PanelPart[]
  doors: PanelPart[]
  /** Interior PAL panels (shelves, drawer boxes) the 3D scene draws. */
  interiorPanels: PanelPart[]
}

export interface WardrobePartsArgs {
  width: number
  height: number
  depth: number
  plintHeight: number
  columns: number
  columnWidths: number[]
  openingType: OpeningType
  isMirrored: boolean
  columnConfigurations?: WardrobeColumnConfiguration[]
}

/**
 * Enumerate every PAL panel the wardrobe 3D scene draws (carcass, dividers,
 * doors, interior shelves/drawers) so the pricing quote matches the model.
 *
 * Pure: same logic the configurator runs (was the WardrobePartsProvider useMemo)
 * so the catalog card and the product page produce identical quotes.
 */
export function computeWardrobeParts(args: WardrobePartsArgs): WardrobeParts {
  const {
    width,
    height,
    depth,
    plintHeight,
    columns,
    columnWidths,
    openingType,
    isMirrored,
    columnConfigurations,
  } = args

  const isNewCollection = openingType === OpeningType.NewCollectionHandle

  const isSingleNarrowColumn =
    columns === 1 && (columnWidths[0] ?? width) < 61

  const isTwoSingleDoorsException =
    isNewCollection &&
    columns === 2 &&
    !!columnConfigurations?.every((col) => col.doorType === 'single')

  const sideDims = computeSidePanelDimensions({
    width,
    height,
    depth,
    isNewCollection,
    isSingleNarrowColumn,
    isTwoSingleDoorsException,
    isMirrored,
  })

  const topDims = computeTopPanelDimensions({
    width,
    depth,
    isNewCollection,
  })

  const SPLIT_DOOR_THRESHOLD = 61
  const baseWidths =
    columnWidths.length === columns
      ? columnWidths
      : Array.from({ length: columns }, () => width / columns)

  const columnDividers: PanelPart[] = []
  if (columns >= 2) {
    for (let i = 0; i < columns - 1; i++) {
      const isFat =
        isNewCollection &&
        baseWidths[i] >= SPLIT_DOOR_THRESHOLD &&
        baseWidths[i + 1] >= SPLIT_DOOR_THRESHOLD

      const divDims = computeColumnDividerDimensions({
        columnHeight: isFat ? height + 2 : height,
        columnDepth: isFat ? depth + 5 : depth,
        plintHeight,
      })
      columnDividers.push({ ...divDims, quantity: 1 })
    }
  }

  const doorHeight = height - plintHeight

  const doors: PanelPart[] = []
  for (let i = 0; i < columns; i++) {
    const colConfig = columnConfigurations?.[i]
    if (colConfig?.hasDoor === false) continue

    const colWidth = baseWidths[i]
    const isSplit = colWidth >= SPLIT_DOOR_THRESHOLD

    const isSingleNarrowForDoor =
      isNewCollection &&
      ((columns === 1 && colWidth < SPLIT_DOOR_THRESHOLD) ||
        (isTwoSingleDoorsException &&
          ((!isMirrored && i === 0) || (isMirrored && i === 1))))

    const dims = computeDoorDimensions({
      columnWidth: colWidth,
      doorHeight,
      isSplit,
      isNewCollection,
      isSingleNarrow: isSingleNarrowForDoor,
    })

    dims.panels.forEach((p) => doors.push({ ...p, quantity: 1 }))
    if (dims.handlePanel) {
      doors.push({ ...dims.handlePanel, quantity: 1, isHandle: true })
    }
  }

  return {
    thickness: sideDims.thickness,
    sidePanels: {
      left: { ...sideDims.left, quantity: 1 },
      right: { ...sideDims.right, quantity: 1 },
    },
    topPanel: {
      width: topDims.width,
      height: topDims.height,
      quantity: 1,
    },
    plinth: {
      width,
      height: plintHeight,
      quantity: 1,
    },
    backPanel: {
      width,
      height,
      quantity: 1,
    },
    columnDividers,
    doors,
    interiorPanels: computeInteriorPanels({
      columnWidths: baseWidths,
      width,
      columns,
      depth,
      height,
      columnConfigurations,
    }),
  }
}